import {
  collectQuestionBlockNodes,
  findQuestionMetaNodes,
  getQuestionBlockRangesFromHtml,
  isHandoutOptionElement,
  serializeQuestionBlockRoot
} from './handoutQuestionBlocks'
import { extractHandoutPaginationPlan } from './handoutPagination'

export const OPTION_LAYOUT_CHOICES = [
  { value: 1, label: '每行一个', icon: '▥' },
  { value: 2, label: '每行两个', icon: '▥▥' },
  { value: 4, label: '每行四个', icon: '▥▥▥▥' }
]

const OPTION_BASE_STYLE = 'font-size:16px;line-height:1.8;color:#111827;box-sizing:border-box;'

const OPTION_LAYOUT_INLINE_STYLE = {
  1: `${OPTION_BASE_STYLE}display:block;width:100%;margin:2px 0 0;`,
  2: `${OPTION_BASE_STYLE}display:inline-block;width:calc(50% - 8px);margin:2px 8px 0 0;vertical-align:top;`,
  4: `${OPTION_BASE_STYLE}display:inline-block;width:calc(25% - 9px);margin:2px 6px 0 0;vertical-align:top;`
}

export function normalizeOptionLayout(value) {
  const n = Number(value) || 1
  return [1, 2, 4].includes(n) ? n : 1
}

export { isHandoutOptionElement } from './handoutQuestionBlocks'

export function findOptionsWrapper(metaNode, blockId) {
  if (!metaNode || !blockId) return null
  let current = metaNode.nextElementSibling
  while (current) {
    if (current.classList && current.classList.contains('handout-inline-question')) break
    if (current.classList && current.classList.contains('handout-question-options')) {
      const id = current.getAttribute('data-handout-question-block')
      if (!id || id === blockId) return current
    }
    current = current.nextElementSibling
  }
  return null
}

function inferLayoutFromStyle(style) {
  const normalized = String(style || '').toLowerCase().replace(/\s+/g, '')
  if (normalized.includes('width:calc(25%') || normalized.includes('width:calc(25%-')) return 4
  if (normalized.includes('width:calc(50%') || normalized.includes('width:calc(50%-')) return 2
  return 1
}

function readLayoutFromOptionNodes(optionNodes) {
  const first = optionNodes && optionNodes[0]
  if (!first) return null
  const fromOption = first.getAttribute('data-handout-option-layout')
  if (fromOption) return normalizeOptionLayout(fromOption)
  const fromStyle = inferLayoutFromStyle(first.getAttribute('style'))
  return fromStyle !== 1 ? fromStyle : null
}

export function pickPreferredLayoutValue(planVal, domVal) {
  const p = planVal != null ? normalizeOptionLayout(planVal) : null
  const d = domVal != null ? normalizeOptionLayout(domVal) : null
  if (p == null) return d || 1
  if (d == null) return p
  if (p === d) return p
  if (p === 1) return d
  if (d === 1) return p
  return p
}

function mergeLayoutValueMaps(planMap, domMap) {
  const merged = {}
  const keys = new Set([
    ...Object.keys(domMap || {}),
    ...Object.keys(planMap || {})
  ])
  keys.forEach(key => {
    merged[key] = pickPreferredLayoutValue(planMap && planMap[key], domMap && domMap[key])
  })
  return merged
}

export function getBlockOptionLayout(metaNode) {
  if (!metaNode) return 1
  const blockId = metaNode.getAttribute('data-handout-question-block')
  const { optionNodes } = partitionBlockNodes(collectQuestionBlockNodes(metaNode, blockId))
  const fromOptions = readLayoutFromOptionNodes(optionNodes)
  if (fromOptions != null && fromOptions !== 1) return fromOptions

  const wrapper = findOptionsWrapper(metaNode, blockId)
  if (wrapper) {
    const fromWrapper = normalizeOptionLayout(wrapper.getAttribute('data-handout-option-layout'))
    if (fromWrapper !== 1) return fromWrapper
  }

  const fromMeta = metaNode.getAttribute('data-handout-option-layout')
  if (fromMeta) {
    const metaLayout = normalizeOptionLayout(fromMeta)
    if (metaLayout !== 1) return metaLayout
  }

  const inferred = inferLayoutFromRenderedOptions(metaNode, blockId)
  if (inferred !== 1) return inferred
  if (fromMeta) return normalizeOptionLayout(fromMeta)
  if (fromOptions != null) return fromOptions
  return 1
}

export function blockHasOptions(metaNode, blockId) {
  if (!metaNode || !blockId) return false
  const nodes = collectQuestionBlockNodes(metaNode, blockId)
  return partitionBlockNodes(nodes).optionNodes.length > 0
}

export function partitionBlockNodes(nodes) {
  const stemNodes = []
  const optionNodes = []
  let optionsWrapper = null
  ;(nodes || []).forEach((node, index) => {
    if (index === 0) return
    if (node.classList && node.classList.contains('handout-question-options')) {
      optionsWrapper = node
      Array.from(node.children || []).forEach(child => {
        if (isHandoutOptionElement(child)) optionNodes.push(child)
      })
      return
    }
    if (isHandoutOptionElement(node)) {
      optionNodes.push(node)
    } else {
      stemNodes.push(node)
    }
  })
  return { stemNodes, optionNodes, optionsWrapper }
}

function copyBlockIdentity(fromNode, toNode) {
  ;['data-handout-question-block', 'data-question-id', 'data-question-title'].forEach(attr => {
    const value = fromNode.getAttribute(attr)
    if (value) toNode.setAttribute(attr, value)
  })
}

export function getOptionLayoutInlineStyle(layout) {
  const cols = normalizeOptionLayout(layout)
  return OPTION_LAYOUT_INLINE_STYLE[cols] || OPTION_LAYOUT_INLINE_STYLE[1]
}

export function applyOptionLayoutToOptionNodes(optionNodes, layout, metaNode) {
  const cols = normalizeOptionLayout(layout)
  const inlineStyle = getOptionLayoutInlineStyle(cols)
  ;(optionNodes || []).forEach(node => {
    node.classList.add('handout-question-option')
    node.setAttribute('data-handout-question-option', 'true')
    node.setAttribute('data-handout-option-layout', String(cols))
    node.setAttribute('style', inlineStyle)
    if (metaNode) copyBlockIdentity(metaNode, node)
  })
}

export function unwrapOptionsWrapper(optionsWrapper) {
  if (!optionsWrapper || !optionsWrapper.parentNode) return []
  const parent = optionsWrapper.parentNode
  const optionNodes = Array.from(optionsWrapper.children || []).filter(isHandoutOptionElement)
  optionNodes.forEach(node => parent.insertBefore(node, optionsWrapper))
  optionsWrapper.remove()
  return optionNodes
}

export function applyOptionLayoutToBlockInRoot(root, blockId, layout) {
  if (!root || !blockId) return false
  const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
  if (!meta) return false

  const layoutVal = normalizeOptionLayout(layout)
  meta.setAttribute('data-handout-option-layout', String(layoutVal))

  const nodes = collectQuestionBlockNodes(meta, blockId)
  let { optionNodes, optionsWrapper } = partitionBlockNodes(nodes)
  if (optionsWrapper) {
    optionNodes = unwrapOptionsWrapper(optionsWrapper)
  }
  if (!optionNodes.length) return false

  applyOptionLayoutToOptionNodes(optionNodes, layoutVal, meta)
  return true
}

export function collectOptionLayoutMapFromRoot(root) {
  const byBlockId = {}
  const byQuestionId = {}
  if (!root) return { byBlockId, byQuestionId }
  findQuestionMetaNodes(root).forEach(metaNode => {
    const blockId = metaNode.getAttribute('data-handout-question-block')
    const questionId = metaNode.getAttribute('data-question-id')
    const layout = getBlockOptionLayout(metaNode)
    if (blockId) byBlockId[blockId] = layout
    if (questionId) byQuestionId[questionId] = layout
  })
  return { byBlockId, byQuestionId }
}

export function mergeLayoutMapsPreferPlan(existing, dom) {
  return {
    byBlockId: mergeLayoutValueMaps(existing && existing.byBlockId, dom && dom.byBlockId),
    byQuestionId: mergeLayoutValueMaps(existing && existing.byQuestionId, dom && dom.byQuestionId)
  }
}

export function inferLayoutFromRenderedOptions(metaNode, blockId) {
  if (!metaNode || !blockId) return 1
  const { optionNodes } = partitionBlockNodes(collectQuestionBlockNodes(metaNode, blockId))
  if (!optionNodes.length) return 1
  const fromStyle = inferLayoutFromStyle(optionNodes[0].getAttribute('style'))
  if (fromStyle !== 1) return fromStyle
  if (typeof window === 'undefined' || optionNodes.length < 2) return 1
  const first = optionNodes[0].getBoundingClientRect()
  const second = optionNodes[1].getBoundingClientRect()
  if (!first.width || !second.width) return 1
  if (Math.abs(first.top - second.top) > 12 || first.left >= second.left) return 1
  const container = metaNode.parentElement || metaNode
  const containerWidth = container.getBoundingClientRect().width
  if (!containerWidth) return 1
  const pairWidth = first.width + second.width
  const ratio = pairWidth / containerWidth
  if (ratio > 0.82) return 4
  if (ratio > 0.42) return 2
  return 1
}

export function resolveBlockOptionLayout(blockId, questionId, metaNode, layoutMap) {
  if (layoutMap && blockId && layoutMap.byBlockId && layoutMap.byBlockId[blockId] != null) {
    return normalizeOptionLayout(layoutMap.byBlockId[blockId])
  }
  if (layoutMap && questionId && layoutMap.byQuestionId && layoutMap.byQuestionId[questionId] != null) {
    return normalizeOptionLayout(layoutMap.byQuestionId[questionId])
  }
  if (metaNode) return getBlockOptionLayout(metaNode)
  return 1
}

export function applyOptionLayoutMapToRoot(root, layoutMap) {
  if (!root || !layoutMap) return
  findQuestionMetaNodes(root).forEach(metaNode => {
    const blockId = metaNode.getAttribute('data-handout-question-block')
    const questionId = metaNode.getAttribute('data-question-id')
    if (!blockId || !blockHasOptions(metaNode, blockId)) return
    const layout = resolveBlockOptionLayout(blockId, questionId, metaNode, layoutMap)
    applyOptionLayoutToBlockInRoot(root, blockId, layout)
  })
}

export function applyOptionLayoutsFromPlan(root, plan) {
  if (!root || !plan || !plan.optionLayouts) return
  applyOptionLayoutMapToRoot(root, plan.optionLayouts)
  const byQuestionId = plan.optionLayouts.byQuestionId || {}
  Object.keys(byQuestionId).forEach(questionId => {
    const meta = root.querySelector(`.handout-inline-question[data-question-id="${questionId}"]`)
    if (!meta) return
    const blockId = meta.getAttribute('data-handout-question-block')
    if (!blockId) return
    applyOptionLayoutToBlockInRoot(root, blockId, byQuestionId[questionId])
  })
}

export function upsertOptionLayoutInPlan(plan, blockId, questionId, layout) {
  const nextPlan = plan && typeof plan === 'object' ? { ...plan } : {}
  const optionLayouts = {
    byBlockId: { ...(nextPlan.optionLayouts && nextPlan.optionLayouts.byBlockId) },
    byQuestionId: { ...(nextPlan.optionLayouts && nextPlan.optionLayouts.byQuestionId) }
  }
  const layoutVal = normalizeOptionLayout(layout)
  if (blockId) optionLayouts.byBlockId[blockId] = layoutVal
  if (questionId) optionLayouts.byQuestionId[questionId] = layoutVal
  nextPlan.optionLayouts = optionLayouts
  return nextPlan
}

export function mergeOptionLayoutsIntoPlan(plan, layoutMap) {
  if (!layoutMap) return plan || null
  const next = plan && typeof plan === 'object' ? { ...plan } : {}
  const existing = next.optionLayouts || { byBlockId: {}, byQuestionId: {} }
  next.optionLayouts = mergeLayoutMapsPreferPlan(existing, layoutMap)
  return next
}

export function ensureOptionLayoutsInRoot(root, layoutMap) {
  if (!root) return
  if (layoutMap) {
    applyOptionLayoutMapToRoot(root, layoutMap)
    return
  }
  findQuestionMetaNodes(root).forEach(metaNode => {
    const blockId = metaNode.getAttribute('data-handout-question-block')
    if (!blockId || !blockHasOptions(metaNode, blockId)) return
    const layout = getBlockOptionLayout(metaNode)
    applyOptionLayoutToBlockInRoot(root, blockId, layout)
  })
}

export function buildOptionsContainerHtml(optionsInnerHtml) {
  return optionsInnerHtml || ''
}

export function buildOptionParagraphHtml(opt, blockId, questionId, title, escapeHtml, layout = 1) {
  const key = escapeHtml(opt.key)
  const valueHtml = opt.valueHtml || ''
  const cols = normalizeOptionLayout(layout)
  const style = getOptionLayoutInlineStyle(cols)
  return `<p class="handout-question-option" data-handout-question-option="true" data-handout-option-layout="${cols}" data-handout-question-block="${blockId}" data-question-id="${questionId}" data-question-title="${title}" style="${style}">${key}.&nbsp;${valueHtml}</p>`
}

export function ensureOptionLayoutsInHtml(html) {
  const { root, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root) return html
  let layoutMap = plan && plan.optionLayouts ? plan.optionLayouts : null
  const collected = collectOptionLayoutMapFromRoot(root)
  layoutMap = mergeLayoutMapsPreferPlan(layoutMap, collected)
  ensureOptionLayoutsInRoot(root, layoutMap)
  const nextPlan = mergeOptionLayoutsIntoPlan(plan, layoutMap)
  return serializeQuestionBlockRoot(root, nextPlan)
}

export function applyOptionLayoutInHtml(html, blockId, layout) {
  const { root, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root || !applyOptionLayoutToBlockInRoot(root, blockId, layout)) return html
  const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
  const questionId = meta ? meta.getAttribute('data-question-id') : ''
  const nextPlan = upsertOptionLayoutInPlan(plan, blockId, questionId, layout)
  return serializeQuestionBlockRoot(root, nextPlan)
}

export function getQuestionOptionLayoutFromHtml(html, blockId) {
  const plan = extractHandoutPaginationPlan(html)
  if (plan && plan.optionLayouts && plan.optionLayouts.byBlockId && plan.optionLayouts.byBlockId[blockId] != null) {
    return normalizeOptionLayout(plan.optionLayouts.byBlockId[blockId])
  }
  const { root } = getQuestionBlockRangesFromHtml(html)
  if (!root || !blockId) return 1
  const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
  if (!meta) return 1
  const questionId = meta.getAttribute('data-question-id')
  if (plan && plan.optionLayouts && questionId && plan.optionLayouts.byQuestionId
      && plan.optionLayouts.byQuestionId[questionId] != null) {
    return normalizeOptionLayout(plan.optionLayouts.byQuestionId[questionId])
  }
  return getBlockOptionLayout(meta)
}

export function questionBlockHasOptionsInHtml(html, blockId) {
  const { root } = getQuestionBlockRangesFromHtml(html)
  if (!root || !blockId) return false
  const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
  return meta ? blockHasOptions(meta, blockId) : false
}
